/**
 * The /app/practice-pulse route component (Practice Pulse → Practice Ops overhaul, 2026-10-30).
 *
 * Plan: docs/plans/2026-10-30-practice-ops-plan.md §6
 *
 * Renders EXACTLY ONE page:
 *   redesign/PracticeOpsPage.vue — the new board (Board, Wall, Operatory and Phone views of one board)
 *   PracticePulse.vue            — the classic page, the fallback
 * decided by utils/practiceOps/newPage.js practiceOpsUsesNewPage (the 'practiceOps' gate — the SAME function Layout.vue and
 * App.vue ask before they hide the app shell for the Wall): Super Admins get the new page unless they picked
 * "Classic view" (localStorage teamio_practice_ops_ui = 'old'); everyone else follows STAGE.practiceOps and
 * their one office choice (teamio_ui_choice:<employeeID>) — uiVersion.js. It ships at 'preview', so only
 * Super Admins see the new page. The classic-side link hides itself for an office user unless the stage is
 * 'invite' / 'default' (FollowupsUiToggle). The route's path, name and permission subject stay
 * `practice-pulse` — only the component changes (src/Routes.js).
 *
 * ── WHY A .js RENDER FUNCTION, NOT A .vue WRAPPER ───────────────────────────────────────────────
 * Each page mounts its own <slideout-panel> outlet, as every route page does. Exactly one page is
 * ever rendered here, so exactly one outlet is ever alive — the invariant
 * cypress/node-tests/slideout-outlet-guard.test.js protects. That guard identifies a page ROOT by
 * "no other .vue imports it"; a .vue wrapper would make both pages look embedded. A route component
 * in .js keeps both as the page roots they really are. (PracticeOpsPage.vue `extends` PracticePulse.vue,
 * which makes the classic file importable by another .vue — so the classic outlet carries a `v-if`;
 * see mountsSlideoutOutlet in PracticePulse.vue.)
 */
import PracticePulse from './PracticePulse.vue';
import PracticeOpsPage from './redesign/PracticeOpsPage.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import { browserStorage } from '@/utils/followups/uiVersion.js';
import { practiceOpsUsesNewPage } from '@/utils/practiceOps/newPage';
import { switchUi } from '@/utils/followups/uiSwitch.js';

export default {
  name: 'PracticeOpsRoute',

  data() {
    // Bumped on every Classic / New click so the gate re-reads storage.
    return { layoutVersion: 0 };
  },

  computed: {
    isSuperAdminUser() {
      const user = this.$store.state.customer && this.$store.state.customer.user;
      return !!(user && user.Role === 'Super Admin');
    },
    // The employee ID the office choice is stored under (a route component has no mixin).
    officeUserKey() {
      return this.$store.state.customer ? this.$store.state.customer.userID : null;
    },
    useNew() {
      // eslint-disable-next-line no-unused-expressions
      this.layoutVersion;
      // The SAME decision Layout.vue and App.vue ask (utils/practiceOps/newPage.js) before they hide the shell for the Wall.
      return practiceOpsUsesNewPage({ isSuperAdmin: this.isSuperAdminUser, userKey: this.officeUserKey, storage: browserStorage() });
    },
  },

  methods: {
    // A Super Admin re-renders in place; an office user's switch reloads the page (uiSwitch.js).
    setLayout(target) {
      const how = switchUi('practiceOps', target, { isSuperAdmin: this.isSuperAdminUser, userKey: this.officeUserKey });
      if (how === 'rerender') this.layoutVersion += 1;
    },
  },

  render(h) {
    if (this.useNew) {
      return h(PracticeOpsPage, {
        key: 'new',
        on: { 'switch-layout': this.setLayout },
      });
    }
    const children = [];
    // The way to the new page from the classic one. FollowupsUiToggle decides by role: always shown to
    // a Super Admin, shown to an office user only while the area is at 'invite' / 'default'.
    children.push(h(FollowupsUiToggle, {
      key: 'to-new',
      style: { marginBottom: '10px' },
      props: { target: 'new', pageKey: 'practiceOps', noteText: 'Super Admin: a redesigned Practice Ops board is ready to check.' },
      on: { toggle: this.setLayout },
    }));
    children.push(h(PracticePulse, { key: 'old' }));
    return h('div', { attrs: { 'data-test': 'practice-pulse-classic' } }, children);
  },
};
